import { EFormMainElements } from "./enum/e-form-main-elements";
import { PasswordResetValidator } from "./validators/password-reset.validator";
import { UsernameValidator } from "./validators/username.validator";
import { CaptchaValidator } from "./validators/captcha.validator";
import { fromEvent, Observable } from "rxjs";
import { FieldConfigModel } from "../../utils/typescript/model/field/field-config.model";
import { AddressAutocompletionPlugin } from "./plugins/address-autocompletion.plugin";
import { ZipCityAutocompletionPlugin } from "./plugins/zip-city-autocompletion.plugin";

declare let hintCodes: Map<number, string>;

class FormController {
  protected formElement!: HTMLFormElement;
  protected elementList: Map<EFormMainElements, NodeListOf<Element>> =
    new Map();
  protected formSubmitObservable: Observable<Event> | undefined;
  protected validationRuleset: FieldConfigModel[] = [];
  protected validationFunctions: ((
    setFieldHintCb: (errorNumber: number[], element: HTMLElement[]) => void,
  ) => void)[] = [];
  protected hintCodesMap: Map<number, string> | undefined;
  protected hasHints = false;
  protected isSubmitValidationEnabled = false;
  protected enabledValidators = new Map<string, boolean>();
  protected enabledPlugins = new Map<string, boolean>();

  /*
   * This special field must be removed completely when the origin validation is removed and the new validation of this FormController is used.
   * Attention: If this is true the form validation is off and the submission is disabled in the FormController.
   */
  protected isNoSubmit = false;

  lookupFor(formSelector: string) {
    this.formElement = document.querySelector(formSelector) as HTMLFormElement;
    if (!this.formElement) {
      return;
    }

    this.getEnabledValidators();
    this.getEnabledPlugins();

    this.init();
  }

  protected init() {
    this.formSubmitObservable = fromEvent(this.formElement, "submit");

    // Attention: If this is true the form validation and the submission events in the controller are off.
    this.isNoSubmit = this.formElement.classList.contains("no-submit");

    this.elementList = this.getDefaultElementList(this.formElement);

    this.buildValidationRules();
    this.buildHintCodes();

    this.addValidators();
    this.addPlugins();

    if (!this.isNoSubmit) {
      this.disableFormSubmitByDefault();
      this.enableSubmitValidation();
    }
  }

  protected getEnabledValidators() {
    this.enabledValidators.set(
      "passwordReset",
      this.formElement.classList.contains("validator-password-reset"),
    );
    this.enabledValidators.set(
      "username",
      this.formElement.classList.contains("validator-username"),
    );
    this.enabledValidators.set(
      "captcha",
      this.formElement.classList.contains("validator-captcha"),
    );
  }

  protected getEnabledPlugins() {
    this.enabledPlugins.set(
      "addressAutocompletion",
      this.formElement.classList.contains("plugin-address-autocompletion"),
    );
    this.enabledPlugins.set(
      "zipCityAutocompletion",
      this.formElement.classList.contains("plugin-zip-city-autocompletion"),
    );
  }

  protected addValidators() {
    if (this.enabledValidators.get("passwordReset")) {
      const passwordResetValidator = new PasswordResetValidator(
        this.formElement,
        this.validationRuleset.filter(
          (ruleset) => ruleset.fieldName === "newPassword",
        ),
      );
      this.validationFunctions.push(
        ...passwordResetValidator.getValidationFunctions(),
      );
    }

    if (this.enabledValidators.get("username")) {
      const usernameValidator = new UsernameValidator(
        this.formElement,
        this.validationRuleset.filter(
          (ruleset) => ruleset.fieldName === "username",
        ),
      );
      this.validationFunctions.push(
        ...usernameValidator.getValidationFunctions(),
      );
    }

    if (this.enabledValidators.get("captcha")) {
      const captchaValidator = new CaptchaValidator(this.formElement);
      this.validationFunctions.push(
        ...captchaValidator.getValidationFunctions(),
      );
    }
  }

  protected addPlugins() {
    if (this.enabledPlugins.get("addressAutocompletion"))
      new AddressAutocompletionPlugin(this.formElement);
    if (this.enabledPlugins.get("zipCityAutocompletion"))
      new ZipCityAutocompletionPlugin(this.formElement);
  }

  protected disableFormSubmitByDefault() {
    if (this.isNoSubmit) {
      return;
    }

    this.formSubmitObservable?.subscribe((event: Event) =>
      event.preventDefault(),
    );
  }

  getDefaultElementList(
    formElement: HTMLFormElement,
  ): Map<EFormMainElements, NodeListOf<Element>> {
    const list = new Map<EFormMainElements, NodeListOf<Element>>();
    list.set(
      EFormMainElements.ERROR_ELEMENTS,
      formElement.querySelectorAll(".hint-list"),
    );
    list.set(
      EFormMainElements.INPUT_BASE,
      formElement.querySelectorAll(".input-base"),
    );

    return list;
  }

  protected buildHintCodes() {
    if (typeof hintCodes === "undefined")
      console.warn("form-validator.ts: No hintCodes Map available.");
    this.hintCodesMap =
      typeof hintCodes === "undefined" ? new Map() : hintCodes;
  }

  protected buildValidationRules() {
    const passwordConfig = new FieldConfigModel();
    passwordConfig.fieldName = "newPassword";

    passwordConfig.rules = [
      {
        ruleName: "Minimale Passwortlänge",
        ruleShortName: "min-password-length",
        regex: /^.{8,}/,
        passed: false,
      },
      {
        ruleName: "Kleinbuchstaben als key",
        ruleShortName: "lower-chars",
        regex: /[a-z]/,
        passed: false,
      },
      {
        ruleName: "Großbuchstaben als key",
        ruleShortName: "upper-chars",
        regex: /[A-Z]/,
        passed: false,
      },
      {
        ruleName: "Zahlen als key",
        ruleShortName: "numbers",
        regex: /\d/,
        passed: false,
      },
      {
        ruleName: "Spezialbuchstaben als key",
        ruleShortName: "special-chars",
        regex: /[^a-zA-Z\d]/,
        passed: false,
      },
    ];

    const usernameConfig = new FieldConfigModel();
    usernameConfig.fieldName = "username";
    usernameConfig.rules = [
      {
        ruleName: "Maximale Länge",
        ruleShortName: "max-length",
        regex: /^.{60,}$/,
        passed: false,
      },
    ];

    this.validationRuleset = [passwordConfig, usernameConfig];
  }

  protected enableSubmitValidation() {
    this.isSubmitValidationEnabled = true;

    if (!this.formElement) {
      return console.error("form element for password validation not found");
    }

    this.formSubmitObservable?.subscribe(async () => {
      this.clearHints();
      this.hasHints = false;

      for (const validation of this.validationFunctions) {
        await validation(this.setFieldHint.bind(this)); // need to bind, because the methods used as callback
      }

      if (!this.hasHints) {
        this.formElement.submit();
      }
    });
  }

  protected clearHints() {
    this.elementList
      .get(EFormMainElements.INPUT_BASE)
      ?.forEach((inputBase) => inputBase.classList.remove("has-error"));

    // update hint elements in map
    this.elementList.set(
      EFormMainElements.ERROR_ELEMENTS,
      this.formElement.querySelectorAll(".hint-list"),
    );

    this.elementList
      .get(EFormMainElements.ERROR_ELEMENTS)
      ?.forEach((hintListElement) => {
        const templateElementCache = hintListElement.querySelector(
          ".input-field-hint.invisible",
        ) as Node;
        hintListElement.textContent = "";
        hintListElement.append(templateElementCache);
      });
  }

  protected setFieldHint(
    hintCodes: number[],
    inputFieldsForHint: HTMLElement[],
  ) {
    for (const inputElement of inputFieldsForHint) {
      const template =
        inputElement?.parentElement?.parentElement?.querySelector(
          ".input-field-hint",
        );
      if (!template) {
        console.error("No .input-field-hint found to put the hint message in");
        return;
      }

      inputElement.parentElement?.classList.add("has-error");
      this.hasHints = true;

      for (const code of hintCodes) {
        const hintField = template.cloneNode(true) as HTMLElement;
        if (hintField) {
          const fieldHintText = hintField.querySelector(".field-hint-text");
          if (fieldHintText)
            fieldHintText.textContent = this.hintCodesMap?.get(code) || "";
        }

        if (inputElement.parentElement?.classList.contains("has-error")) {
          hintField.classList.add("has-error");
        }

        if (inputElement.parentElement?.classList.contains("disabled")) {
          hintField.classList.add("is-disabled");
        }

        inputElement?.parentElement?.parentElement
          ?.querySelector(".hint-list")
          ?.append(hintField);
        setTimeout(() => hintField.classList.remove("invisible")); // fadein behaviour
      }
    }
  }
}

new FormController().lookupFor("form.validate");
